import React, { useState, useEffect } from "react";
import axios from "axios";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import {
  API_ops_assistant_confirm_action,
  API_get_report_tabs,
} from "../../../constants";
import { DATE_RANGE_GROUPS } from "../reporting/dateRangePresets";
import { toast } from "react-toastify";

const NEW_TAB_SENTINEL = "__new__";

const WIDGET_TYPE_OPTIONS = [
  { value: "line", label: "Line" },
  { value: "bar", label: "Bar" },
  { value: "pie", label: "Pie" },
  { value: "table", label: "Table" },
  { value: "cards", label: "Cards" },
];

export default function SaveReportProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  onStatusChange,
  currentChartWidgetType,
  currentChartLayout,
}) {
  const summary = proposal.summary || {};

  const [status, setStatus] = useState(proposal.status || "pending");
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState(summary.name || "Untitled Report");
  const [preset, setPreset] = useState(summary.date_range_preset || "custom");
  const [customStart, setCustomStart] = useState(summary.custom_start_date || "");
  const [customEnd, setCustomEnd] = useState(summary.custom_end_date || "");
  // Prefer the currently-visible chart type (reflects user's in-chat dropdown
  // edits) over the LLM's original recommend_visualization pick.
  const [widgetType, setWidgetType] = useState(
    currentChartWidgetType || summary.widget_type || "line"
  );

  const [tabs, setTabs] = useState([]);
  const [tabsLoaded, setTabsLoaded] = useState(false);
  // Selected tab name ("" = no tab, NEW_TAB_SENTINEL = creating a new one)
  const [tabChoice, setTabChoice] = useState(summary.tab_name || "");
  const [newTabName, setNewTabName] = useState("");

  useEffect(() => {
    if (
      (proposal.status === "cancelled" || proposal.status === "rejected") &&
      status === "pending"
    ) {
      setStatus("cancelled");
    }
  }, [proposal.status]);

  // Fetch existing tabs so the user can pick one
  useEffect(() => {
    let cancelled = false;
    const axiosConfig = getDefaultAxiosConfig(homeStore);
    axios
      .get(API_get_report_tabs, {
        params: { store_id: homeStore },
        ...axiosConfig,
      })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setTabs(list);
        setTabsLoaded(true);
        // If the LLM suggested a tab_name that doesn't exist yet, preload the
        // new-tab path so it gets created on confirm
        if (summary.tab_name && !list.some((t) => t.name === summary.tab_name)) {
          setTabChoice(NEW_TAB_SENTINEL);
          setNewTabName(summary.tab_name);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setTabsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [homeStore]);

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const payload = {
        store_id: homeStore,
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action,
      };

      if (action === "confirm") {
        const overrides = {};
        if (name.trim() !== (summary.name || "").trim()) {
          overrides.name = name.trim();
        }
        if (preset !== summary.date_range_preset) {
          overrides.date_range_preset = preset;
        }
        if (preset === "custom") {
          if (customStart !== (summary.custom_start_date || "")) {
            overrides.custom_start_date = customStart || null;
          }
          if (customEnd !== (summary.custom_end_date || "")) {
            overrides.custom_end_date = customEnd || null;
          }
        }
        const resolvedTabName =
          tabChoice === NEW_TAB_SENTINEL ? newTabName.trim() : tabChoice;
        if (resolvedTabName !== (summary.tab_name || "")) {
          overrides.tab_name = resolvedTabName;
        }
        if (widgetType !== summary.widget_type) {
          overrides.widget_type = widgetType;
        }
        // Save the layout the user is looking at, even if the model omitted it.
        if (currentChartLayout && currentChartLayout !== summary.chart_layout) {
          overrides.chart_layout = currentChartLayout;
        }
        if (Object.keys(overrides).length > 0) {
          payload.overrides = overrides;
        }
      }

      const res = await postRightworkAPI(
        API_ops_assistant_confirm_action,
        payload
      );
      if (res.status === 200) {
        const newStatus = action === "confirm" ? "confirmed" : "cancelled";
        setStatus(newStatus);
        onStatusChange?.(proposal.proposal_id, newStatus);
      } else {
        toast.error("Failed to save report.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message ||
          err?.response?.data?.error ||
          "Failed to save report."
      );
    }
    setLoading(false);
  };

  if (status === "confirmed") {
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 my-2">
        <CheckCircleIcon fontSize="small" className="text-green-600 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-medium">Saved to dashboard</div>
          <div className="text-xs opacity-80">{name.trim()}</div>
        </div>
      </div>
    );
  }

  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Save cancelled</span>
      </div>
    );
  }

  const isNewTab = tabChoice === NEW_TAB_SENTINEL;
  // YYYY-MM-DD strings from <input type="date"> sort lexicographically
  const customDatesOutOfOrder =
    preset === "custom" &&
    customStart &&
    customEnd &&
    customStart > customEnd;
  const confirmDisabled =
    loading ||
    !name.trim() ||
    (preset === "custom" && (!customStart || !customEnd)) ||
    customDatesOutOfOrder ||
    (isNewTab && !newTabName.trim());

  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <BookmarkAddIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">
          Save Report to Dashboard
        </span>
      </div>

      <div className="px-3 py-2 space-y-2">
        {/* Name */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-0.5">
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={255}
            className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white"
            placeholder="Report name"
          />
        </div>

        {/* Date range */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-0.5">
            Date range
          </label>
          <select
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white"
          >
            {DATE_RANGE_GROUPS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {preset !== "custom" && (
            <div className="text-[11px] text-gray-500 mt-0.5">
              Re-resolves each time the dashboard loads.
            </div>
          )}
        </div>

        {preset === "custom" && (
          <div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-0.5">
                  Start
                </label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  max={customEnd || undefined}
                  className={`w-full text-sm border rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white ${
                    customDatesOutOfOrder ? "border-red-400" : "border-gray-300"
                  }`}
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-0.5">
                  End
                </label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  min={customStart || undefined}
                  className={`w-full text-sm border rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white ${
                    customDatesOutOfOrder ? "border-red-400" : "border-gray-300"
                  }`}
                />
              </div>
            </div>
            {customDatesOutOfOrder && (
              <div className="text-[11px] text-red-500 mt-0.5">
                Start date must be on or before end date.
              </div>
            )}
          </div>
        )}

        {/* Tab */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-0.5">
            Dashboard tab
          </label>
          <select
            value={tabChoice}
            onChange={(e) => {
              const val = e.target.value;
              setTabChoice(val);
              if (val !== NEW_TAB_SENTINEL) setNewTabName("");
            }}
            disabled={!tabsLoaded}
            className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white disabled:bg-gray-50"
          >
            <option value="">All Reports (no tab)</option>
            {tabs.map((t) => (
              <option key={t.id} value={t.name}>
                {t.name}
              </option>
            ))}
            <option value={NEW_TAB_SENTINEL}>+ New tab…</option>
          </select>
          {isNewTab && (
            <input
              type="text"
              value={newTabName}
              onChange={(e) => setNewTabName(e.target.value)}
              maxLength={255}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 mt-1 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white"
              placeholder="New tab name"
              autoFocus
            />
          )}
        </div>

        {/* Chart type */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-0.5">
            Chart type
          </label>
          <select
            value={widgetType}
            onChange={(e) => setWidgetType(e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white"
          >
            {WIDGET_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Details footer */}
        {summary.metrics_label && (
          <div className="text-[11px] text-gray-500 pt-1">
            <span className="text-gray-400">Metrics: </span>
            {summary.metrics_label}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
        <button
          onClick={() => handleAction("reject")}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => handleAction("confirm")}
          disabled={confirmDisabled}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loading && <CircularProgress size={14} sx={{ color: "white" }} />}
          Save
        </button>
      </div>
    </div>
  );
}
